<template>
    <!-- The /rentrisk-verification identity form, shared by its own page, the
         PMC company registration (RAC-10417) and the self report's
         Experian Authentication step (86bcfa7n0). -->
    <div class="shadow-xl rounded-lg p-6 bg-white">
        <div v-if="instructions.callout" class="mb-5 rounded-md bg-blue-50 p-4">
            <p class="text-sm text-blue-800">{{ instructions.callout }}</p>
        </div>
        <p class="text-lg font-bold text-gray-700 sm:text-lg sm:truncate text-center">
            {{ introText }}
        </p>
        <ul class="text-gray-700 list-disc pl-5 pt-5">
            <li v-for="bullet in instructions.bullets" :key="bullet">{{ bullet }}</li>
        </ul>
            
        <form @submit.prevent="update" class="mt-20 space-y-8 divide-y divide-gray-200">
            <input type="hidden" id="user_prefs" name="user_prefs"> <input type="hidden" id="user_prefs2" name="user_prefs2">
            <div class="space-y-8 divide-y divide-gray-200">
                <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div class="sm:col-span-2">
                        <label for="first_name" v-bind:class="(form.errors.first_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            First Name <span class="text-red-700">*</span>
                        </label>
                        <div class="mt-1">
                            <input id="first_name" v-model="form.first_name" :error="form.errors.first_name" type="text" v-bind:class="(form.errors.first_name)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                            <p v-text="form.errors.first_name" class="text-red-500 text-sm"></p>
                        </div>
                    </div>
                    <div class="sm:col-span-2">
                        <label for="middle_name" v-bind:class="(form.errors.middle_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            Middle Name
                        </label>
                        <div class="mt-1">
                            <input id="middle_name" :disabled="disabled" v-model="form.middle_name" :error="form.errors.middle_name" type="text" :class="{'bg-gray-50 cursor-not-allowed': this.disabled}" v-bind:class="(form.errors.middle_name)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                            <p v-text="form.errors.middle_name" class="text-red-500 text-sm"></p>
                            <input type="checkbox" class="focus:ring-rent-risk-blue-400 h-4 w-4 text-rent-risk-blue-400 border-gray-300 rounded" @click="isDisabled()" /> <span class="text-sm">I do not have a middle name</span>
                        </div>
                    </div>
                    <div class="sm:col-span-2">
                        <span class="flex">
                            <label for="last_name" v-bind:class="(form.errors.last_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                Last Name <span class="text-red-700">*</span>
                            </label>
                            <tool-tip
                                title="Info"
                                description="If you are having trouble authenticating this could be due to a recent name change. Please contact support."
                                ></tool-tip>
                        </span>
                        <div class="mt-1">
                            <input id="last_name" v-model="form.last_name" :error="form.errors.last_name" type="text" v-bind:class="(form.errors.last_name)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                            <p v-text="form.errors.last_name" class="text-red-500 text-sm"></p>
                        </div>
                    </div>
                    <div class="sm:col-span-6">
                        <alert-card type="info">
                            <div>
                                <h3 class="text-sm font-medium text-blue-800">Living Overseas?</h3>
                                <div class="mt-2 text-sm text-blue-700">
                                    <p>If you are currently living overseas, please use your last U.S. based address. Foreign addresses or military (APO or FPO) or DoD addresses overseas will NOT work.</p>
                                </div>
                            </div>
                        </alert-card>
                    </div>
                    <div class="sm:col-span-3">
                        <label for="address-1" v-bind:class="(form.errors.address_1)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            Address 1 <span class="text-red-700">*</span>
                        </label>
                        <div class="mt-1">
                            <input id="address-1" v-model="form.address_1" :error="form.errors.address_1" type="text" v-bind:class="(form.errors.address_1)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                            <p class="text-gray-500 text-sm">Personal address only, do NOT use business address</p>
                            <p v-text="form.errors.address_1" class="text-red-500 text-sm"></p>
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <label for="address-2" v-bind:class="(form.errors.address_2)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            Address 2
                        </label>
                        <div class="mt-1">
                            <input id="address-2" v-model="form.address_2" :error="form.errors.address_2" type="text" v-bind:class="(form.errors.address_2)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <label for="city" v-bind:class="(form.errors.city)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            City <span class="text-red-700">*</span>
                        </label>
                        <div class="mt-1">
                            <input id="city" v-model="form.city" :error="form.errors.city" type="text" v-bind:class="(form.errors.city)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                            <p v-text="form.errors.city" class="text-red-500 text-sm"></p>
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <label for="state" v-bind:class="(form.errors.state)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            State <span class="text-red-700">*</span>
                        </label>
                        <div class="mt-1">
<!--                                    <input id="state" v-model="form.state" :error="form.errors.state" type="text" v-bind:class="(form.errors.state)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">-->
                            <select-input id="state" v-bind:value="form.state" v-model="form.state" :error="form.errors.state" v-bind:class="(form.errors.state)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm'">
                                <option v-for="(state, index) in states" :key="index" :value="index">{{ state }}</option>
                            </select-input>
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <label for="zip_code" v-bind:class="(form.errors.zip_code)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            Zip Code <span class="text-red-700">*</span>
                        </label>
                        <div class="mt-1">
                            <input id="zip_code" v-model="form.zip_code" :error="form.errors.zip_code" type="text" v-bind:class="(form.errors.zip_code)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                            <p v-text="form.errors.zip_code" class="text-red-500 text-sm"></p>
                        </div>
                    </div>
                    <div class="sm:col-span-3">
                        <label for="phone" v-bind:class="(form.errors.phone)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            Phone <span class="text-red-700">*</span>
                        </label>
                        <div class="mt-1">
                            <input id="phone" v-model="form.phone" :error="form.errors.phone" type="text" v-bind:class="(form.errors.phone)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                            <p v-text="form.errors.phone" class="text-red-500 text-sm"></p>
                        </div>
                    </div>
                    <div class="sm:col-span-3"></div>
                    <div class="sm:col-span-3">
                        <label for="dob" v-bind:class="(form.errors.dob)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            Birthday <span class="text-red-700">*</span>
                        </label>
                        <div class="mt-1 relative rounded-md shadow-sm">
                            <div class="absolute left-0 pl-3 flex items-center pointer-events-none" v-bind:class="(form.errors.dob)?'top-3':'inset-y-0'">
                                <font-awesome-icon :icon="faCalendar" class="text-gray-400" />
                            </div>
                            <input id="dob" v-model="form.dob" :error="form.errors.dob" type="date" v-bind:class="(form.errors.dob)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md pl-10'">
                            <p v-text="form.errors.dob" class="text-red-500 text-sm"></p>
                        </div>
                    </div>
                    <div class="sm:col-span-3">
                        <label for="dob_confirmation" v-bind:class="(form.errors.dob_confirmation)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            Confirm Birthday <span class="text-red-700">*</span>
                        </label>
                        <div class="mt-1 relative rounded-md shadow-sm">
                            <div class="absolute left-0 pl-3 flex items-center pointer-events-none" v-bind:class="(form.errors.dob_confirmation)?'top-3':'inset-y-0'">
                                <font-awesome-icon :icon="faCalendar" class="text-gray-400" />
                            </div>
                            <input id="dob_confirmation" v-model="form.dob_confirmation" :error="form.errors.dob_confirmation" type="date" v-bind:class="(form.errors.dob_confirmation)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md pl-10'">
                            <p v-text="form.errors.dob_confirmation" class="text-red-500 text-sm"></p>
                        </div>
                    </div>
                    <div class="sm:col-span-3">
                        <label for="ssn" v-bind:class="(form.errors.ssn)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            SSN <span class="text-red-700">*</span>
                        </label>
                        <div class="mt-1">
                            <input id="ssn" v-model="form.ssn" :error="form.errors.ssn" type="password" v-bind:class="(form.errors.ssn)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                            <p class="text-gray-500 text-sm">Do not include dashes</p>
                            <p class="text-gray-500 text-sm">We do not store your SSN on our servers. Your SSN is used to verify you with RentRisk.</p>
                            <p v-text="form.errors.ssn" class="text-red-500 text-sm"></p>
                        </div>
                    </div>
                    <div class="sm:col-span-3">
                        <label for="ssn_confirmation" v-bind:class="(form.errors.ssn_confirmation)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                            Confirm SSN <span class="text-red-700">*</span>
                        </label>
                        <div class="mt-1">
                            <input id="ssn_confirmation" v-model="form.ssn_confirmation" :error="form.errors.ssn_confirmation" type="password" v-bind:class="(form.errors.ssn_confirmation)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'">
                            <p class="text-gray-500 text-sm">Do not include dashes</p>
                            <p class="text-gray-500 text-sm">We do not store your SSN_confirmation on our servers. Your SSN_confirmation is used to verify you with RentRisk.</p>
                            <p v-text="form.errors.ssn_confirmation" class="text-red-500 text-sm"></p>
                        </div>
                    </div>

                    <div v-if="attempt.attempts < 3" class="sm:col-span-6">
                        <alert-card :type="(attempt.attempts === 0) ? 'error' : 'warning'">
                            <div class="">
                                <h3 :class="[ (attempt.attempts === 0) ? 'text-red-800' : 'text-yellow-800', 'text-sm font-bold' ]">Verification Attempts Remaining</h3>
                                <div v-if="attempt.attempts > 0" :class="[ (attempt.attempts === 0) ? 'text-red-700' : 'text-yellow-700', 'mt-2 text-sm' ]">
                                    You have {{attempt.attempts}} remaining attempts for RentRisk Verification within the next 24 hours.
                                </div>
                                <div v-else :class="[ (attempt.attempts === 0) ? 'text-red-700' : 'text-yellow-700', 'mt-2 text-sm' ]">
                                    You are currently locked out due to multiple unsuccessful verification attempts. You will be able to try again after {{attempt.remaining_lockout_time}}.
                                </div>
                            </div>
                        </alert-card>
                    </div>
                </div>
            </div>
            <div class="pt-5 grid grid-cols-6 gap-4">
                <div class="col-end-7 col-span-2 justify-end flex">
                    <inertia-link :href="cancelHref" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400">Cancel</inertia-link>
                    <form-button :form="form" type="submit" class="ml-3">
                        Save
                    </form-button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCalendar } from '@fortawesome/pro-light-svg-icons'
import { useForm } from '@inertiajs/vue3'
import FormButton from '@/Shared/FormButton.vue'
import SelectInput from "@/Shared/SelectInput.vue"
import ToolTip from '@/Shared/ToolTip.vue'
import AlertCard from "@/Shared/AlertCard.vue"
import { verificationInstructions } from '@/Pages/ExperianUser/verificationInstructions'

export default {
    components: {
        FormButton,
        SelectInput,
        ToolTip,
        AlertCard,
        FontAwesomeIcon,
    },
    props: {
        states: Object,
        attempt: Object,
        // RAC-10417: the PMC company registration reuses this identity form.
        // Defaults keep the individual verification flow unchanged.
        submitRoute: {
            type: String,
            default: 'experian-verification.store',
        },
        companyName: {
            type: String,
            default: '',
        },
        // 86bcfa7n0: the self report runs this form inside its own steps.
        submitParams: {
            type: [Number, String, Array, Object],
            default: undefined,
        },
        cancelHref: {
            type: String,
            default: () => route('properties.index'),
        },
        introText: {
            type: String,
            default: 'Please read the following points before continuing with your account setup.',
        },
    },
    data() {
        return {
            faCalendar,
            form: useForm({
                user_prefs: null,
                user_prefs2: null,
                first_name: null,
                middle_name: null,
                last_name: null,
                address_1: null,
                address_2: null,
                city: null,
                state: null,
                zip_code: null,
                phone: null,
                dob: null,
                dob_confirmation: null,
                ssn: null,
                ssn_confirmation: null,
            }),
            disabled: false,
            showExperianHelp: false,
        }
    },
    computed: {
        instructions() {
            return verificationInstructions({
                role: this.$page.props.user.role,
                companyName: this.companyName,
            })
        },
    },
    methods: {
        isDisabled() {
            this.disabled = !this.disabled;
            return this.form.middle_name = '';
        },
        update() {
            this.form.user_prefs = adx.validate('user_prefs');
            this.form.user_prefs2 = adx.validate('user_prefs2');
            this.form.post(this.route(this.submitRoute, this.submitParams))
        },
        autofill(person) {
            this.form.first_name = person.first;
            this.form.middle_name = person.middle;
            this.form.last_name = person.last;
            this.form.address_1 = person.address;
            this.form.city = person.city;
            this.form.state = person.state;
            this.form.zip_code = person.zip;
            this.form.phone = person.phone;
            this.form.dob = person.dob;
            this.form.ssn = person.ssn;
        }
    },
}
</script>
